import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import type { ClassRoom, Student, TimetableSlot } from '@/lib/types';
import { useTeachers } from '@/hooks/useTeachers';
import { useProfile } from '@/hooks/useProfile';
import WeeklyTimetable from '@/pages/timetable/components/WeeklyTimetable';
import SlotFormModal from '@/pages/timetable/components/SlotFormModal';
import {
  buildPrintDays,
  buildTeacherGroups,
  resolveSlotStudents,
} from '@/pages/timetable/utils';
import { printWeeklyTimetable } from '@/pages/timetable/printTimetable';

const INTEGRATED_VIEW = 'all';

export default function Timetable() {
  const [slots, setSlots] = useState<TimetableSlot[]>([]);
  const [classes, setClasses] = useState<ClassRoom[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [classStudentIds, setClassStudentIds] = useState<Record<string, string[]>>({});
  const [slotStudentIds, setSlotStudentIds] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<string>('');

  const [formOpen, setFormOpen] = useState(false);
  const [editingSlot, setEditingSlot] = useState<TimetableSlot | null>(null);

  const { teachers } = useTeachers();
  const { profile } = useProfile();

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [slotsRes, classesRes, studentsRes, classStudentsRes, slotStudentsRes] =
        await Promise.all([
          supabase
            .from('timetable')
            .select('*')
            .order('day_of_week', { ascending: true })
            .order('start_time', { ascending: true }),
          supabase.from('classes').select('*'),
          supabase.from('students').select('*').order('name', { ascending: true }),
          supabase.from('class_students').select('class_id, student_id'),
          supabase.from('timetable_students').select('slot_id, student_id'),
        ]);
      if (slotsRes.error) throw slotsRes.error;
      if (classesRes.error) throw classesRes.error;
      if (studentsRes.error) throw studentsRes.error;
      if (classStudentsRes.error) throw classStudentsRes.error;
      if (slotStudentsRes.error) throw slotStudentsRes.error;
      setSlots((slotsRes.data as TimetableSlot[]) || []);
      setClasses((classesRes.data as ClassRoom[]) || []);
      setStudents((studentsRes.data as Student[]) || []);

      const classIds: Record<string, string[]> = {};
      ((classStudentsRes.data as Array<{ class_id: string; student_id: string }>) || []).forEach(
        (row) => {
          if (!classIds[row.class_id]) classIds[row.class_id] = [];
          classIds[row.class_id].push(row.student_id);
        }
      );
      setClassStudentIds(classIds);

      const slotIds: Record<string, string[]> = {};
      ((slotStudentsRes.data as Array<{ slot_id: string; student_id: string }>) || []).forEach(
        (row) => {
          if (!slotIds[row.slot_id]) slotIds[row.slot_id] = [];
          slotIds[row.slot_id].push(row.student_id);
        }
      );
      setSlotStudentIds(slotIds);
    } catch (err) {
      setError('시간표를 불러오지 못했어요.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const teacherMap = useMemo(
    () => new Map(teachers.map((t) => [t.id, t.full_name || '이름 없음'])),
    [teachers]
  );
  const classMap = useMemo(() => new Map(classes.map((c) => [c.id, c])), [classes]);
  const studentMap = useMemo(() => new Map(students.map((s) => [s.id, s.name])), [students]);

  const namesByIds = useCallback(
    (ids: string[]) => ids.map((id) => studentMap.get(id) || '').filter(Boolean),
    [studentMap]
  );

  const classRosterNames = useMemo(() => {
    const out: Record<string, string[]> = {};
    Object.entries(classStudentIds).forEach(([classId, ids]) => {
      out[classId] = namesByIds(ids);
    });
    return out;
  }, [classStudentIds, namesByIds]);

  const slotRosterNames = useMemo(() => {
    const out: Record<string, string[]> = {};
    Object.entries(slotStudentIds).forEach(([slotId, ids]) => {
      out[slotId] = namesByIds(ids);
    });
    return out;
  }, [slotStudentIds, namesByIds]);

  const studentsBySlot = useMemo(
    () => resolveSlotStudents(slots, classRosterNames, slotRosterNames),
    [slots, classRosterNames, slotRosterNames]
  );

  function openEditSlot(slot: TimetableSlot) {
    setEditingSlot(slot);
    setFormOpen(true);
  }

  const groups = useMemo(
    () => buildTeacherGroups(classes, slots, (id) => teacherMap.get(id) || '이름 없음'),
    [classes, slots, teacherMap]
  );

  const activeView = selected || groups[0]?.id || INTEGRATED_VIEW;
  const isIntegrated = activeView === INTEGRATED_VIEW;
  const activeGroup = groups.find((g) => g.id === activeView) || null;
  const viewSlots = isIntegrated ? slots : activeGroup?.slots || [];

  function handlePrint() {
    const title = isIntegrated
      ? '통합 주간 시간표'
      : `${activeGroup?.name ?? ''} 선생님 주간 시간표`;
    printWeeklyTimetable({
      title,
      subtitle: profile?.academy_name || '',
      days: buildPrintDays(viewSlots, classMap, teacherMap, studentsBySlot, isIntegrated),
    });
  }

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-bold text-foreground-950">주간 시간표</h2>
          <p className="mt-1 text-sm text-foreground-500">
            담당 선생님별 시간표를 확인하고, 통합 시간표로도 볼 수 있어요.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handlePrint}
            disabled={viewSlots.length === 0}
            className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <i className="ri-printer-line text-base"></i>
            인쇄하기
          </button>
          <Link
            to="/vacation-timetable"
            className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap text-sm font-semibold text-primary-600 hover:text-primary-700"
          >
            방학특강 시간표 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>

      {error && (
        <div className="flex items-center justify-between rounded-md bg-accent-100 px-4 py-3 text-sm text-accent-800">
          <span>{error}</span>
          <button
            onClick={loadData}
            className="cursor-pointer whitespace-nowrap font-semibold underline"
          >
            다시 시도
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex h-48 items-center justify-center">
          <i className="ri-loader-4-line animate-spin text-2xl text-foreground-500"></i>
        </div>
      ) : slots.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-background-200 bg-white py-16 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-calendar-line text-2xl"></i>
          </div>
          <p className="mt-4 text-sm font-medium text-foreground-700">아직 등록된 일정이 없어요.</p>
          <p className="mt-1 text-xs text-foreground-400">
            반 관리에서 각 반의 시간표를 등록해 보세요.
          </p>
          <Link
            to="/classes"
            className="mt-4 inline-flex cursor-pointer items-center gap-1 whitespace-nowrap text-sm font-semibold text-primary-600 hover:text-primary-700"
          >
            반 관리로 이동 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            {groups.map((g) => {
              const active = !isIntegrated && g.id === activeView;
              return (
                <button
                  key={g.id}
                  onClick={() => setSelected(g.id)}
                  className={`inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${
                    active
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  <i className="ri-user-line text-xs"></i>
                  {g.name}
                  <span
                    className={`rounded-full px-1.5 text-[10px] leading-4 ${
                      active ? 'bg-background-50/25 text-background-50' : 'bg-white text-foreground-500'
                    }`}
                  >
                    {g.slots.length}
                  </span>
                </button>
              );
            })}
            <button
              onClick={() => setSelected(INTEGRATED_VIEW)}
              className={`inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full px-3.5 py-2 text-sm font-semibold transition-colors ${
                isIntegrated
                  ? 'bg-accent-500 text-background-50'
                  : 'bg-accent-100 text-accent-800 hover:bg-accent-200'
              }`}
            >
              <i className="ri-stack-line text-xs"></i>
              통합 시간표
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-2 text-sm text-foreground-600">
            <i className="ri-information-line text-secondary-500"></i>
            {isIntegrated ? (
              <span>
                모든 선생님의 시간표를 합쳐서 보여줘요. 각 수업에 담당 선생님이 표시돼요.
              </span>
            ) : (
              <span>
                <strong className="font-semibold text-foreground-900">{activeGroup?.name}</strong>{' '}
                선생님의 주간 시간표예요. 총 {viewSlots.length}개의 수업이 있어요.
              </span>
            )}
            <span className="text-foreground-400">
              수업 카드의 연필 아이콘을 눌러 학생 명단을 수정할 수 있어요.
            </span>
          </div>

          <WeeklyTimetable
            slots={viewSlots}
            classMap={classMap}
            studentsBySlot={studentsBySlot}
            teacherMap={teacherMap}
            showTeacher={isIntegrated}
            onEdit={openEditSlot}
          />

          <SlotFormModal
            open={formOpen}
            slot={editingSlot}
            classRoom={editingSlot ? classMap.get(editingSlot.class_id) || null : null}
            students={students}
            classStudentIds={
              editingSlot ? classStudentIds[editingSlot.class_id] || [] : []
            }
            slotStudentIds={editingSlot ? slotStudentIds[editingSlot.id] || [] : []}
            onClose={() => setFormOpen(false)}
            onSaved={() => {
              setFormOpen(false);
              loadData();
            }}
            onDeleted={() => {
              setFormOpen(false);
              loadData();
            }}
          />
        </>
      )}
    </div>
  );
}